import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import { supabase } from "../../supabase";
import "./MyPage.css";
import { DESIGN_SAVE_LIMIT, FILE_SAVE_LIMIT, STORAGE_RETENTION_DAYS } from "../../config/features";
import { countSaved, restoreOverLimit } from "../../utils/storageQuota";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useModal } from "../../context/ModalContext";
import CSSelect from "../../components/CSSelect";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";
const ITEMS_PER_PAGE = 24;

/* 10.13 처럼 짧게. 올해가 아니면 25.10.13 */
function shortDate(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const md = `${d.getMonth() + 1}.${d.getDate()}`;
    return d.getFullYear() === new Date().getFullYear() ? md : `${String(d.getFullYear()).slice(2)}.${md}`;
}

export default function MyPageHome() {
    const { user } = useContext(AuthContext);
    const history = useHistory();
    const { t } = useTranslation();
    const { showAlert } = useModal();

    const [files, setFiles] = useState([]);
    const [designs, setDesigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dataIsTrash, setDataIsTrash] = useState(false); // 지금 들고 있는 목록이 휴지통 것인지
    const [sortOption, setSortOption] = useState("new");

    const [savedCount, setSavedCount] = useState({ designs: 0, files: 0 });
    // 칩 숫자 (휴지통 아닌 목록 기준 — 휴지통을 보고 있어도 유지)
    const [liveCount, setLiveCount] = useState({ designs: 0, files: 0 });

    // "all" | "files" | "designs" | "trash"
    const [viewMode, setViewMode] = useState("all");
    const isTrash = viewMode === "trash";
    const [page, setPage] = useState(0);

    const [selectMode, setSelectMode] = useState(false);
    const [selectedFileIds, setSelectedFileIds] = useState([]);
    const [selectedDesignIds, setSelectedDesignIds] = useState([]);

    const [openMenu, setOpenMenu] = useState(null);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmText, setConfirmText] = useState("");
    const [confirmAction, setConfirmAction] = useState(null);

    useEffect(() => {
        if (!user) return;
        // 목록이 바뀔 때마다(삭제·복구) 저장 개수를 다시 센다
        countSaved(user.id).then(setSavedCount).catch(() => {});
    }, [user, files, designs]);

    /* ===== 디자인 + STL 로딩 (폴더 구분 없이 전부) ===== */
    useEffect(() => {
        if (!user) return;
        let cancelled = false;

        async function load() {
            setLoading(true);

            let dq = supabase
                .from("designs")
                .select("id, name, thumbnail, updated_at, is_deleted, deleted_at")
                .eq("user_id", user.id)
                .eq("is_deleted", isTrash);
            if (sortOption === "old") dq = dq.order("updated_at", { ascending: true });
            else if (sortOption === "name") dq = dq.order("name", { ascending: true });
            else dq = dq.order("updated_at", { ascending: false });

            const fq = supabase
                .from("user_files")
                .select("*")
                .eq("user_id", user.id)
                .eq("is_deleted", isTrash);

            const [dRes, fRes] = await Promise.all([dq, fq]);
            if (cancelled) return;

            if (fRes.error) console.error(fRes.error);
            setDesigns(dRes.data || []);
            setFiles(fRes.data || []);
            setDataIsTrash(isTrash);
            setLoading(false);
        }

        load();
        return () => { cancelled = true; };
    }, [user, isTrash, sortOption]);

    useEffect(() => {
        if (!dataIsTrash && !loading) setLiveCount({ designs: designs.length, files: files.length });
    }, [dataIsTrash, loading, designs, files]);

    useEffect(() => {
        setSelectedFileIds([]);
        setSelectedDesignIds([]);
        setOpenMenu(null);
        setPage(0);
    }, [viewMode]);

    useEffect(() => {
        if (!selectMode) {
            setSelectedFileIds([]);
            setSelectedDesignIds([]);
        } else {
            setOpenMenu(null);
        }
    }, [selectMode]);

    /* ⋯ 메뉴: 바깥 클릭 / Esc 로 닫기 */
    useEffect(() => {
        if (!openMenu) return;
        const onDown = (e) => {
            if (!e.target.closest || !e.target.closest(".mp-card-menu-wrap")) setOpenMenu(null);
        };
        const onKey = (e) => {
            if (e.key === "Escape") setOpenMenu(null);
        };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [openMenu]);

    /* 휴지통에서 되살릴 때 10개를 넘으면 막는다. 넘으면 true */
    async function blockedByLimit(counts) {
        if (user?.role === "admin") return false;
        const over = await restoreOverLimit(user.id, counts);
        if (!over) return false;
        showAlert(t(over.kind === "designs" ? "mypage.home.restoreDesignLimit" : "mypage.home.restoreFileLimit", { limit: over.limit }));
        return true;
    }

    function goEdit(fileId) {
        history.push(`/cutter?fileId=${fileId}`);
    }

    function goDesign(designId) {
        history.push(`/design?id=${designId}`);
    }

    function openConfirm({ text, onConfirm }) {
        setOpenMenu(null);
        setConfirmText(text);
        setConfirmAction(() => onConfirm);
        setConfirmOpen(true);
    }

    /* ===== 다운로드 ===== */
    async function downloadFile(file) {
        setOpenMenu(null);
        // 1️⃣ 다운로드 가능 여부 체크 (admin은 무제한)
        if (user?.role !== "admin") {
            const formData = new FormData();
            formData.append("user_id", user.id);

            const checkRes = await fetch(`${API_BASE}/can-download`, {
                method: "POST",
                body: formData,
            });

            const check = await checkRes.json();

            if (!check.allowed) {
                showAlert(
                    t("mypage.home.storageExceeded", { used: check.used_mb, limit: check.limit_mb })
                );
                return;
            }
        }

        // 2️⃣ 기존 다운로드 로직
        const { data, error } = await supabase.storage
            .from("user-files")
            .createSignedUrl(file.storage_path, 60);

        if (error || !data?.signedUrl) {
            showAlert(t("mypage.home.downloadFailed"));
            return;
        }

        const a = document.createElement("a");
        a.href = data.signedUrl;
        a.download = file.file_name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }

    /* ===== STL: 휴지통 이동 ===== */
    function deleteFile(file) {
        openConfirm({
            text: t("mypage.home.confirmDeleteFile", { name: file.file_name }),
            onConfirm: async () => {
                await supabase
                    .from("user_files")
                    .update({
                        is_deleted: true,
                        deleted_at: new Date().toISOString(),
                    })
                    .eq("id", file.id)
                    .eq("user_id", user.id);

                setFiles((prev) => prev.filter((f) => f.id !== file.id));
            },
        });
    }

    /* ===== STL: 복구 ===== */
    function restoreFile(file) {
        openConfirm({
            text: t("mypage.home.confirmRestoreFile", { name: file.file_name }),
            onConfirm: async () => {
                if (await blockedByLimit({ files: 1 })) return;
                // 보관 기간(30일)은 STL 의 created_at 으로 센다 → 되살린 시점부터 다시 시작
                await supabase
                    .from("user_files")
                    .update({
                        is_deleted: false,
                        deleted_at: null,
                        created_at: new Date().toISOString(),
                    })
                    .eq("id", file.id)
                    .eq("user_id", user.id);

                setFiles((prev) => prev.filter((f) => f.id !== file.id));
            }
        });
    }

    /* ===== STL: 완전 삭제 ===== */
    function deletePermanently(file) {
        openConfirm({
            text: t("mypage.home.confirmDeletePermanently", { name: file.file_name }),
            onConfirm: async () => {
                await fetch(
                    `${API_BASE}/delete-file-permanently?file_id=${file.id}`,
                    { method: "POST" }
                );

                setFiles((prev) => prev.filter((f) => f.id !== file.id));
            }
        });
    }

    /* ===== 디자인: 휴지통 이동 / 복구 / 완전 삭제 ===== */
    function deleteDesign(d) {
        openConfirm({
            text: t("mypage.home.confirmDeleteDesign", { name: d.name }),
            onConfirm: async () => {
                await supabase.from("designs").update({ is_deleted: true, deleted_at: new Date().toISOString() }).eq("id", d.id);
                setDesigns((prev) => prev.filter((x) => x.id !== d.id));
                setSelectedDesignIds((prev) => prev.filter((id) => id !== d.id));
            },
        });
    }

    function restoreDesign(d) {
        openConfirm({
            text: t("mypage.home.confirmRestoreFile", { name: d.name }),
            onConfirm: async () => {
                if (await blockedByLimit({ designs: 1 })) return;
                await supabase.from("designs").update({ is_deleted: false, deleted_at: null, updated_at: new Date().toISOString() }).eq("id", d.id);
                setDesigns((prev) => prev.filter((x) => x.id !== d.id));
            },
        });
    }

    function deleteDesignPermanently(d) {
        openConfirm({
            text: t("mypage.home.confirmDeleteDesign", { name: d.name }),
            onConfirm: async () => {
                await supabase.from("designs").delete().eq("id", d.id);
                setDesigns((prev) => prev.filter((x) => x.id !== d.id));
                setSelectedDesignIds((prev) => prev.filter((id) => id !== d.id));
            },
        });
    }

    /* ===== 일괄 처리 (선택 모드) ===== */
    function bulkTrash() {
        const fIds = selectedFileIds;
        const dIds = selectedDesignIds;
        openConfirm({
            text: fIds.length > 0
                ? t("mypage.home.confirmBulkDelete", { count: fIds.length + dIds.length })
                : t("mypage.home.confirmBulkDeleteDesign", { count: dIds.length }),
            onConfirm: async () => {
                const nowIso = new Date().toISOString();
                if (fIds.length > 0) {
                    await supabase
                        .from("user_files")
                        .update({ is_deleted: true, deleted_at: nowIso })
                        .in("id", fIds)
                        .eq("user_id", user.id);
                    setFiles((prev) => prev.filter((f) => !fIds.includes(f.id)));
                }
                for (const id of dIds) {
                    await supabase.from("designs").update({ is_deleted: true, deleted_at: nowIso }).eq("id", id);
                }
                if (dIds.length > 0) setDesigns((prev) => prev.filter((d) => !dIds.includes(d.id)));
                setSelectedFileIds([]);
                setSelectedDesignIds([]);
            },
        });
    }

    function bulkRestore() {
        const fIds = selectedFileIds;
        const dIds = selectedDesignIds;
        openConfirm({
            text: t("mypage.home.confirmBulkRestore", { count: fIds.length + dIds.length }),
            onConfirm: async () => {
                if (await blockedByLimit({ files: fIds.length, designs: dIds.length })) return;
                const nowIso = new Date().toISOString();
                if (fIds.length > 0) {
                    await supabase
                        .from("user_files")
                        .update({ is_deleted: false, deleted_at: null, created_at: nowIso })
                        .in("id", fIds)
                        .eq("user_id", user.id);
                    setFiles((prev) => prev.filter((f) => !fIds.includes(f.id)));
                }
                for (const id of dIds) {
                    await supabase.from("designs").update({ is_deleted: false, deleted_at: null, updated_at: nowIso }).eq("id", id);
                }
                if (dIds.length > 0) setDesigns((prev) => prev.filter((d) => !dIds.includes(d.id)));
                setSelectedFileIds([]);
                setSelectedDesignIds([]);
            },
        });
    }

    function bulkDeletePermanently() {
        const fIds = selectedFileIds;
        const dIds = selectedDesignIds;
        openConfirm({
            text: t("mypage.home.confirmBulkDeletePermanently", { count: fIds.length + dIds.length }),
            onConfirm: async () => {
                for (const id of fIds) {
                    try {
                        await fetch(`${API_BASE}/delete-file-permanently?file_id=${id}`, { method: "POST" });
                    } catch (e) {
                        console.error("delete failed:", id, e);
                    }
                }
                if (fIds.length > 0) setFiles((prev) => prev.filter((f) => !fIds.includes(f.id)));
                for (const id of dIds) {
                    await supabase.from("designs").delete().eq("id", id);
                }
                if (dIds.length > 0) setDesigns((prev) => prev.filter((d) => !dIds.includes(d.id)));
                setSelectedFileIds([]);
                setSelectedDesignIds([]);
            },
        });
    }

    /* ===== 화면에 보일 카드 목록 (STL + AI 도안 한 줄로) ===== */
    const fileItems = files.map((f) => ({
        key: `file-${f.id}`, kind: "file", id: f.id, name: f.file_name, thumb: f.thumbnail_url,
        date: isTrash ? (f.deleted_at || f.created_at) : f.created_at, raw: f,
    }));
    const designItems = designs.map((d) => ({
        key: `design-${d.id}`, kind: "design", id: d.id, name: d.name, thumb: d.thumbnail,
        date: isTrash ? (d.deleted_at || d.updated_at) : d.updated_at, raw: d,
    }));
    let items = viewMode === "files" ? fileItems : viewMode === "designs" ? designItems : [...fileItems, ...designItems];
    items = [...items].sort((a, b) => {
        if (sortOption === "name") return String(a.name || "").localeCompare(String(b.name || ""));
        const diff = new Date(a.date) - new Date(b.date);
        return sortOption === "old" ? diff : -diff;
    });

    const totalPages = Math.max(1, Math.ceil(items.length / ITEMS_PER_PAGE));
    const pageItems = items.slice(page * ITEMS_PER_PAGE, (page + 1) * ITEMS_PER_PAGE);

    const selectedCount = selectedFileIds.length + selectedDesignIds.length;
    const allSelected = items.length > 0 && selectedCount === items.length;

    function isSelected(it) {
        return it.kind === "file" ? selectedFileIds.includes(it.id) : selectedDesignIds.includes(it.id);
    }

    function toggleSelect(it) {
        const setter = it.kind === "file" ? setSelectedFileIds : setSelectedDesignIds;
        setter((prev) => (prev.includes(it.id) ? prev.filter((id) => id !== it.id) : [...prev, it.id]));
    }

    function toggleSelectAll() {
        if (allSelected) {
            setSelectedFileIds([]);
            setSelectedDesignIds([]);
        } else {
            setSelectedFileIds(items.filter((it) => it.kind === "file").map((it) => it.id));
            setSelectedDesignIds(items.filter((it) => it.kind === "design").map((it) => it.id));
        }
    }

    function openItem(it) {
        if (selectMode) {
            toggleSelect(it);
            return;
        }
        if (isTrash) return;
        if (it.kind === "file") goEdit(it.id);
        else goDesign(it.id);
    }

    const usedRatio = Math.min(1, Math.max(
        savedCount.designs / DESIGN_SAVE_LIMIT,
        savedCount.files / FILE_SAVE_LIMIT,
    ));

    const chips = [
        { key: "all", label: t("mypage.home.tabAll"), count: liveCount.designs + liveCount.files },
        { key: "files", label: t("mypage.home.chipStl"), count: liveCount.files },
        { key: "designs", label: t("mypage.home.chipDesigns"), count: liveCount.designs },
        { key: "trash", label: `🗑 ${t("mypage.home.tabTrash")}` },
    ];

    return (
        <div className="mp-drive">
            {/* ===== 머리 + 저장공간 ===== */}
            <div className="mp-page-head">
                <div>
                    <h1 className="mp-page-title">{t("mypage.home.myDrive")}</h1>
                    <p className="mp-page-sub">{t("mypage.home.desc")}</p>
                </div>
                <div className="mp-quota">
                    <small>{t("mypage.home.storage")}</small>
                    <b>
                        {t("mypage.account.storageDesigns")} {savedCount.designs}<i>/{DESIGN_SAVE_LIMIT}</i>
                        {" · "}STL {savedCount.files}<i>/{FILE_SAVE_LIMIT}</i>
                    </b>
                    <div className="mp-bar"><span style={{ width: `${Math.round(usedRatio * 100)}%` }} /></div>
                    <small className="mp-quota-note">
                        {t("mypage.home.retentionRule", { days: STORAGE_RETENTION_DAYS })}
                    </small>
                </div>
            </div>

            {/* ===== 필터 칩 + 선택·정렬 ===== */}
            <div className="mp-toolbar">
                <div className="mp-chips">
                    {chips.map((c) => (
                        <button
                            key={c.key}
                            type="button"
                            className={viewMode === c.key ? "active" : ""}
                            onClick={() => setViewMode(c.key)}
                        >
                            {c.label}{c.count !== undefined ? ` ${c.count}` : ""}
                        </button>
                    ))}
                </div>
                <div className="mp-toolbar-right">
                    <button
                        type="button"
                        className={`mp-pill-btn ${selectMode ? "active" : ""}`}
                        onClick={() => setSelectMode((v) => !v)}
                    >
                        {selectMode ? t("mypage.home.cancel") : t("mypage.home.select")}
                    </button>
                    <CSSelect
                        className="mp-sort"
                        value={sortOption}
                        onChange={setSortOption}
                        options={[
                            { value: "new", label: t("mypage.home.sortNew") },
                            { value: "old", label: t("mypage.home.sortOld") },
                            { value: "name", label: t("mypage.home.sortName") },
                        ]}
                    />
                </div>
            </div>

            <div className="mp-section-title">
                {isTrash ? (
                    <>
                        {t("mypage.home.trash")}
                        <span className="mp-section-hint">{t("mypage.home.trashHint")}</span>
                    </>
                ) : (
                    t("mypage.home.files")
                )}
            </div>

            {(loading || dataIsTrash !== isTrash) ? (
                <div className="mp-drive-loading">{t("mypage.home.loading")}</div>
            ) : items.length === 0 ? (
                isTrash ? (
                    <div className="mp-empty">
                        <div className="mp-empty-icon">🗑</div>
                        <b>{t("mypage.home.trashEmpty")}</b>
                    </div>
                ) : (
                    <div className="mp-empty">
                        <div className="mp-empty-icon">🍪</div>
                        <b>{t("mypage.home.emptyTitle")}</b>
                        <p>{t("mypage.home.emptyDesc")}</p>
                        <div className="mp-empty-actions">
                            <button type="button" className="mp-btn dark" onClick={() => history.push("/cutter")}>
                                {t("nav.cutter")}
                            </button>
                            <button type="button" className="mp-btn outline" onClick={() => history.push("/design")}>
                                {t("nav.design")}
                            </button>
                        </div>
                    </div>
                )
            ) : (
                <div className={`mp-grid ${selectMode ? "selecting" : ""}`}>
                    {pageItems.map((it) => {
                        const selected = isSelected(it);
                        const menuOpen = openMenu === it.key;
                        return (
                            <div className={`mp-card ${selected ? "selected" : ""}`} key={it.key}>
                                <div className="mp-thumb" onClick={() => openItem(it)}>
                                    {it.thumb ? <img src={it.thumb} alt="" loading="lazy" /> : <div className="mp-thumb-empty" />}
                                    <span className={`mp-kind ${it.kind === "file" ? "is-stl" : "is-design"}`}>{it.kind === "file" ? "STL" : t("mypage.home.chipDesigns")}</span>
                                    {selectMode && (
                                        <span className={`mp-check ${selected ? "on" : ""}`} aria-hidden>
                                            {selected ? "✓" : ""}
                                        </span>
                                    )}
                                </div>

                                {!selectMode && (
                                    <div className="mp-card-menu-wrap">
                                        <button
                                            type="button"
                                            className="mp-more"
                                            aria-label="menu"
                                            aria-expanded={menuOpen}
                                            onClick={() => setOpenMenu(menuOpen ? null : it.key)}
                                        >
                                            ⋯
                                        </button>
                                        {menuOpen && (
                                            <div className="mp-card-menu" role="menu">
                                                {!isTrash && it.kind === "file" && (
                                                    <button type="button" role="menuitem" onClick={() => downloadFile(it.raw)}>
                                                        ⬇ {t("mypage.home.download")}
                                                    </button>
                                                )}
                                                {!isTrash && (
                                                    <button
                                                        type="button"
                                                        role="menuitem"
                                                        onClick={() => (it.kind === "file" ? goEdit(it.id) : goDesign(it.id))}
                                                    >
                                                        ✎ {t("mypage.home.continueEdit")}
                                                    </button>
                                                )}
                                                {!isTrash && (
                                                    <button
                                                        type="button"
                                                        role="menuitem"
                                                        className="danger"
                                                        onClick={() => (it.kind === "file" ? deleteFile(it.raw) : deleteDesign(it.raw))}
                                                    >
                                                        🗑 {t("mypage.home.moveToTrash")}
                                                    </button>
                                                )}
                                                {isTrash && (
                                                    <button
                                                        type="button"
                                                        role="menuitem"
                                                        onClick={() => (it.kind === "file" ? restoreFile(it.raw) : restoreDesign(it.raw))}
                                                    >
                                                        ↺ {t("mypage.home.restore")}
                                                    </button>
                                                )}
                                                {isTrash && (
                                                    <button
                                                        type="button"
                                                        role="menuitem"
                                                        className="danger"
                                                        onClick={() => (it.kind === "file" ? deletePermanently(it.raw) : deleteDesignPermanently(it.raw))}
                                                    >
                                                        ✕ {t("mypage.home.deletePermanently")}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="mp-card-name" title={it.name} onClick={() => openItem(it)}>{it.name}</div>
                                <div className="mp-card-date">{shortDate(it.date)}</div>
                            </div>
                        );
                    })}
                </div>
            )}

            {totalPages > 1 && (
                <div className="pagination">
                    <button disabled={page === 0} onClick={() => setPage(page - 1)}>
                        {t("mypage.home.prev")}
                    </button>
                    <span>{page + 1} / {totalPages}</span>
                    <button disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
                        {t("mypage.home.next")}
                    </button>
                </div>
            )}

            {/* ===== 선택 모드 하단 바 ===== */}
            {selectMode && (
                <div className="mp-select-bar">
                    <span className="mp-select-count">{t("mypage.home.selectedCount", { count: selectedCount })}</span>
                    <button type="button" className="mp-btn ghost" onClick={toggleSelectAll} disabled={items.length === 0}>
                        {allSelected ? t("mypage.home.deselectAll") : t("mypage.home.selectAll")}
                    </button>
                    {!isTrash ? (
                        <button type="button" className="mp-btn danger" disabled={selectedCount === 0} onClick={bulkTrash}>
                            {selectedCount}{t("mypage.home.bulkDeleteBtn")}
                        </button>
                    ) : (
                        <>
                            <button type="button" className="mp-btn outline" disabled={selectedCount === 0} onClick={bulkRestore}>
                                {selectedCount}{t("mypage.home.bulkRestoreBtn")}
                            </button>
                            <button type="button" className="mp-btn danger" disabled={selectedCount === 0} onClick={bulkDeletePermanently}>
                                {selectedCount}{t("mypage.home.bulkDeletePermanentlyBtn")}
                            </button>
                        </>
                    )}
                    <button type="button" className="mp-btn dark" onClick={() => setSelectMode(false)}>
                        {t("mypage.home.done")}
                    </button>
                </div>
            )}

            {confirmOpen && (
                <div className="modal-backdrop">
                    <div className="modal confirm">
                        <h3>{t("mypage.home.confirm")}</h3>
                        <p className="confirm-text">{confirmText}</p>
                        <div className="modal-actions">
                            <button onClick={() => setConfirmOpen(false)}>
                                {t("mypage.home.cancel")}
                            </button>
                            <button
                                className="danger"
                                onClick={async () => {
                                    if (confirmAction) {
                                        await confirmAction();
                                    }
                                    setConfirmOpen(false);
                                }}
                            >
                                {t("mypage.home.confirm")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
